/**
 * OracleContext — one polling hub shared by every admin surface.
 *
 * Prevents four different components from each running their own 15s poll.
 * The context runs:
 *   • oracle/summary  every 15s → drives bell count, section badges, health
 *   • oracle/heartbeat every 60s → sends presence heartbeat
 *   • oracle/presence every 60s → team roster
 *
 * Performance polish (Feb 2026):
 *   • All timers pause when the browser tab is hidden (document.visibilityState)
 *     to save bandwidth + battery, and resume on focus with an immediate refresh.
 *   • summary/health is now backend-computed — no client-side re-derivation.
 *
 * Consumers subscribe via `useOracle()`.
 */
import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import {
  oracleSummary,
  oracleHeartbeat,
  oraclePresence,
} from "../lib/api";

const OracleContext = createContext(null);

const SUMMARY_MS = 15_000;
const HEARTBEAT_MS = 60_000;
const PRESENCE_MS = 60_000;

// Map Oracle event categories → top-level admin section keys.
// Used to place a badge dot next to the right sidebar entry.
export const CATEGORY_TO_SECTION = {
  security:    "security",
  jericho:     "security",
  recovery:    "security",
  presence:    "security",
  members:     "members",
  subscribers: "members",
  editorial:   "dossiers",
  email:       "command",
  dispatch:    "command",
  analytics:   "command",
  deployments: "command",
  system:      "command",
  oracle:      "command",
};

const SEV_ORDER = { critical: 0, warning: 1, info: 2, team: 3 };

export const OracleProvider = ({ opToken, userToken, children }) => {
  const [summary, setSummary] = useState(null);
  const [presence, setPresence] = useState(null);
  const [updatedAt, setUpdatedAt] = useState(null);  // client-side wall clock of last successful summary poll
  const summaryTimer = useRef(null);
  const heartbeatTimer = useRef(null);
  const presenceTimer = useRef(null);
  const visibleRef = useRef(typeof document === "undefined" ? true : document.visibilityState === "visible");

  const loadSummary = useCallback(async () => {
    try {
      const r = await oracleSummary(opToken || null, userToken || null);
      setSummary(r);
      setUpdatedAt(Date.now());
    } catch { /* silent */ }
  }, [opToken, userToken]);

  const loadPresence = useCallback(async () => {
    try { setPresence(await oraclePresence(opToken || null, userToken || null)); }
    catch { /* silent */ }
  }, [opToken, userToken]);

  const sendHeartbeat = useCallback(async () => {
    try { await oracleHeartbeat(opToken || null, userToken || null); }
    catch { /* silent — presence is best-effort */ }
  }, [opToken, userToken]);

  const clearTimers = useCallback(() => {
    if (summaryTimer.current) { clearInterval(summaryTimer.current); summaryTimer.current = null; }
    if (heartbeatTimer.current) { clearInterval(heartbeatTimer.current); heartbeatTimer.current = null; }
    if (presenceTimer.current) { clearInterval(presenceTimer.current); presenceTimer.current = null; }
  }, []);

  const startTimers = useCallback(() => {
    clearTimers();
    summaryTimer.current = setInterval(loadSummary, SUMMARY_MS);
    heartbeatTimer.current = setInterval(sendHeartbeat, HEARTBEAT_MS);
    presenceTimer.current = setInterval(loadPresence, PRESENCE_MS);
  }, [clearTimers, loadSummary, sendHeartbeat, loadPresence]);

  useEffect(() => {
    // Initial fetch always
    loadSummary();
    loadPresence();
    sendHeartbeat();
    if (visibleRef.current) startTimers();

    const onVisibility = () => {
      const isVisible = document.visibilityState === "visible";
      visibleRef.current = isVisible;
      if (isVisible) {
        // Immediate refresh on refocus so operator never sees stale data.
        loadSummary();
        loadPresence();
        sendHeartbeat();
        startTimers();
      } else {
        clearTimers();
      }
    };
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      clearTimers();
    };
  }, [loadSummary, loadPresence, sendHeartbeat, startTimers, clearTimers]);

  // Compute per-section badge (counts + worst severity).
  const sectionBadges = React.useMemo(() => {
    const out = { command: null, dossiers: null, members: null, security: null, settings: null };
    if (!summary?.by_category) return out;
    for (const [cat, meta] of Object.entries(summary.by_category)) {
      const sk = CATEGORY_TO_SECTION[cat];
      if (!sk) continue;
      const cur = out[sk] || { count: 0, worst: "info" };
      cur.count += meta.count;
      if (SEV_ORDER[meta.worst] < SEV_ORDER[cur.worst]) cur.worst = meta.worst;
      out[sk] = cur;
    }
    return out;
  }, [summary]);

  const value = React.useMemo(() => ({
    summary,
    presence,
    sectionBadges,
    updatedAt,
    refreshSummary: loadSummary,
    refreshPresence: loadPresence,
  }), [summary, presence, sectionBadges, updatedAt, loadSummary, loadPresence]);

  return <OracleContext.Provider value={value}>{children}</OracleContext.Provider>;
};

export const useOracle = () => {
  const ctx = useContext(OracleContext);
  return ctx || {
    summary: null,
    presence: null,
    sectionBadges: {},
    updatedAt: null,
    refreshSummary: () => {},
    refreshPresence: () => {},
  };
};

export default OracleContext;
